import React from 'react';
import { observer } from 'src/utils/mobx-react';
// fix in LT-2382
// eslint-disable-next-line
import { useAppStateContext } from 'src/appState/AppState';
import { useCommon } from 'src/domains/common/Common';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { SportMeetingsList } from 'src/domains/sportsbook/webview/components/containers/sportMeetingsList/SportMeetingsList';
import { QuickPick } from 'src/domains/sportsbook/webview/components/quickPick/QuickPick';
import {
    FiltersWrapper,
    ContentWrapper,
    BuildRaceCardButtonWrapper,
    SportsMeetingsWrapper,
} from './NewRacingPage.style';
import { FiltersNewView } from 'src/domains/layouts/webview/components/filters/Filters';
import { CompetitionsTrapChallenge } from 'src/domains/sportsbook/webview/components/containers/competitionsTrapChallenge/CompetitionsTrapChallenge';
import { FiltersScrollableWrapper } from 'src/domains/layouts/webview/components/filters/FiltersScrollableWrapper';
import { PriceChangeLegend } from 'src/domains/layouts/webview/components/priceChangeLegend/PriceChangeLegend';
import { MeetingsSpecials } from 'src/domains/sportsbook/webview/components/meetings/specials/MeetingsSpecials';
import { MeetingsAntePost } from 'src/domains/sportsbook/webview/components/meetings/antePost/MeetingsAntePost';
import { getHeader, renderButton } from './NewRacingPage.helpers';
import { SportHeaderStar } from 'src/domains/layouts/webview/components/sportHeader/SportHeader';
import { SportRacesTypeParamType } from 'src/domains/layouts/state/router/newRouter/mainRouteTypes';
import { HorseRacingState } from './NewRacingPage.state';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { EventId } from 'src_common/common/websocket2/id/WebsocketId';
import { SeoPagesContent } from 'src/domains/layouts/webview/components/seoPages/SeoPages';
import { NextRacesSportPage } from 'src/domains/sportsbook/webview/components/nextRaces/NextRacesSportPage';
import { NextRacesState } from 'src/domains/sportsbook/webview/components/nextRaces/NextRaces.state';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { BreakpointsState } from 'src/domains/layouts/state/breakpointsState/BreakpointsState';

interface FilterType {
    key: SportRacesTypeParamType;
    label: string;
    onClick: () => void;
    dataTest: string;
}

const getUkAndIreLabel = (language: LanguagesState): string => {
    return language.getTranslation('sports.racing.uk-ire.label', 'UK & IRE');
};

const getInternationalLabel = (language: LanguagesState): string => {
    return language.getTranslation('sports.racing.international.label', 'International');
};

const getAntePostLabel = (language: LanguagesState): string => {
    return language.getTranslation('sports.racing.future.label', 'Future');
};

const getFilters = (
    language: LanguagesState,
    horseRacingState: HorseRacingState,
    onChangeCallback: (id: SportRacesTypeParamType) => void,
    isTabletOrBigger: boolean,
    sport: 'horseracing' | 'greyhoundracing'
): Array<FilterType> => {
    const { getTranslation } = language;

    const ukAndIreLabel = getUkAndIreLabel(language);

    const internationalLabel = getInternationalLabel(language);

    const quickPickLabel = language.getTranslation('sports.racing.quick-pick.label', 'Quick Pick');

    const buildRacecardLabel = language.getTranslation('sports.racing.build-racecard.label', 'Build a Racecard');

    const trapChallengeBlock: FilterType = {
        key: 'trap-challenge',
        label: getTranslation('sports.racing.trap-challenge.label', 'Trap Challenge'),
        dataTest: getTranslation('sports.racing.trap-challenge.label', 'Trap Challenge'),
        onClick: (): void => onChangeCallback('trap-challenge'),
    };
    const antePostLabel = getAntePostLabel(language);

    const specialsFilter: FilterType = {
        key: 'specials',
        label: getTranslation('sports.racing.specials.label', 'Specials'),
        dataTest: getTranslation('sports.racing.specials.label', 'Specials'),
        onClick: (): void => onChangeCallback('specials'),
    };

    const optionQuickPick: FilterType = {
        key: 'quick-pick',
        label: quickPickLabel,
        dataTest: quickPickLabel,
        onClick: (): void => onChangeCallback('quick-pick'),
    };

    const optionBuildRaceCard: FilterType = {
        key: 'build-racecard',
        label: buildRacecardLabel,
        dataTest: buildRacecardLabel,
        onClick: (): void => onChangeCallback('build-racecard'),
    };

    const optionAntePost: FilterType = {
        key: 'ante-post',
        label: antePostLabel,
        dataTest: antePostLabel,
        onClick: (): void => onChangeCallback('ante-post'),
    };

    const optionUkAndIre: FilterType = {
        key: 'uk-ire',
        label: ukAndIreLabel,
        dataTest: ukAndIreLabel,
        onClick: (): void => onChangeCallback('uk-ire'),
    };

    const optionInternationalLabel: FilterType = {
        key: 'international',
        label: internationalLabel,
        dataTest: internationalLabel,
        onClick: (): void => onChangeCallback('international'),
    };

    let filters = [optionUkAndIre, optionInternationalLabel, optionAntePost];

    if (isTabletOrBigger) {
        const desktopFilters = [...filters.slice(0, 2), optionQuickPick, optionBuildRaceCard, ...filters.slice(2)];
        filters = desktopFilters;
    }

    if (horseRacingState.showTrapChallenge === true) {
        filters.push(trapChallengeBlock);
    }

    if (horseRacingState.showSpecials === true) {
        const specialsFilters = [...filters.slice(0, 2), specialsFilter, ...filters.slice(2)];
        filters = specialsFilters;
    }

    if (sport === 'greyhoundracing') {
        filters = filters.filter((filter) => filter.key !== 'international');
    }

    return filters;
};

interface PropsType {
    sport: 'horseracing' | 'greyhoundracing';
    filterType: SportRacesTypeParamType | null;
    eventId: EventId | null;
}

export const NewSportRacingContent = observer('NewSportRacingContent', (props: PropsType) => {
    const { sport, eventId } = props;

    const appState = useAppStateContext();
    const { appSportsBookState } = appState;
    const common = useCommon();
    const configState = ConfigComponents.get(common).config;
    const languagesState = LanguagesState.get(common);
    const starRouter = StarRouter.get(common);
    const breakpointsState = BreakpointsState.get(common);
    const { sportsbookVariant } = configState.componentVariants;
    const isTabletOrBigger = breakpointsState.tablet.isBiggerOrEq === true;
    const filterType = props.filterType ?? 'uk-ire';
    const state = appSportsBookState.horseRacingState.get(sport);

    const selectedNew = state.selectedNew;
    const buildRaceCard = filterType === 'build-racecard';

    const options = getHeader(sport);
    const isAntePostReverse = filterType === 'ante-post' && configState.isAntePostReverse;

    const filters = getFilters(languagesState, state, state.cleanSelected, isTabletOrBigger, sport);

    const [nextRacesState] = React.useState(() => new NextRacesState(common, sport));
    const isLoading = nextRacesState.isLoadingEventsCollectionEarly;

    return (
        <section className='sport-racing'>
            <SportHeaderStar
                options={options}
                sportId={sport}
                key={`sport-header + ${sport}`}
                isCompetitionPage={false}
                competitionId={null}
            />

            <ContentWrapper
                isAntePostReverse={isAntePostReverse}
                variant={sportsbookVariant}
            >
                <NextRacesSportPage
                    nextRacesState={nextRacesState}
                    sport={sport}
                />

                <SportsMeetingsWrapper
                    data-test='sports-meetings-wrapper'
                    isAntePostReverse={isAntePostReverse}
                    variant={sportsbookVariant}
                >
                    <FiltersWrapper>
                        <FiltersScrollableWrapper>
                            <FiltersNewView
                                select={filterType}
                                filters={filters}
                            />
                        </FiltersScrollableWrapper>
                    </FiltersWrapper>
                    <React.Fragment key='filters'>
                        {filterType === 'uk-ire' || filterType === 'international' ? (
                            <SportMeetingsList
                                key={filterType}
                                sport={sport}
                                selected={selectedNew}
                                isLoading={isLoading}
                                isBuilding={false}
                                shared
                                filterType={filterType}
                            />
                        ) : null}
                        {filterType === 'quick-pick' ? (
                            <QuickPick
                                label={
                                    <I18n
                                        langKey='racing.quick-pick.label'
                                        defaultText='quick pick'
                                    />
                                }
                                key='quick-pick'
                                sport={sport}
                                isLoading={isLoading}
                                shared
                            />
                        ) : null}
                        {filterType === 'build-racecard' ? (
                            <SportMeetingsList
                                key='build-racecard'
                                sport={sport}
                                selected={selectedNew}
                                isBuilding
                                onChoose={state.toggleEvent}
                                isLoading={isLoading}
                                shared
                            />
                        ) : null}
                        {filterType === 'ante-post' ? (
                            <MeetingsAntePost
                                key='ante-post'
                                sport={sport}
                                filterType={filterType}
                                eventId={eventId}
                                isLoading={isLoading}
                                shared
                            />
                        ) : null}
                        {filterType === 'specials' ? (
                            <MeetingsSpecials
                                key='specials'
                                sport={sport}
                                filterType={filterType}
                                isLoading={isLoading}
                                eventId={eventId}
                                shared
                            />
                        ) : null}
                        {filterType === 'trap-challenge' ? (
                            <CompetitionsTrapChallenge
                                key='trap-challenge'
                                isLoading={isLoading}
                                sportEvents={state.trapChallengeEvents}
                                shared
                            />
                        ) : null}
                    </React.Fragment>
                </SportsMeetingsWrapper>
            </ContentWrapper>

            <BuildRaceCardButtonWrapper variant={sportsbookVariant}>
                {selectedNew.racesSelected() > 0 && buildRaceCard
                    ? renderButton(starRouter, state.isBuilding, state.selectedNew, sport, sportsbookVariant)
                    : null}
            </BuildRaceCardButtonWrapper>
            <SeoPagesContent pageSlug={sport} />
            <PriceChangeLegend isRace={true} />
        </section>
    );
});
